/**
 * invoices + invoice_line_items + invoice_tickets + hauler_tenant_invoice_settings
 * — Phase 12 / Task 12.1 invoice data model (master spec §12/§29).
 *
 * The invoice generator reads ONLY billable_events (DEC-013/§88) and groups unbilled
 * PER_BBL + PER_UNIT events by operator/period into invoices. Money is integer cents.
 * All four tables are tenant-scoped + RLS (migration 0054). Invoice-facing strings use
 * the "Paelode Systems" legal brand.
 *
 * Concurrency-safe numbering: hauler_tenant_invoice_settings.sequence is incremented
 * with an atomic INSERT ... ON CONFLICT DO UPDATE SET sequence = sequence + 1 RETURNING
 * (row lock) — never a read-then-write.
 */

import {
  boolean,
  check,
  date,
  index,
  integer,
  numeric,
  pgPolicy,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { jobs } from './jobs.js';

const tenantIsolation = (name: string) =>
  pgPolicy(name, {
    as: 'permissive',
    to: appUserRole,
    for: 'all',
    using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
  });

export const INVOICE_STATUSES = ['DRAFT', 'SENT', 'PAID', 'VOID'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_GROUPINGS = ['PER_OPERATOR_PERIOD', 'PER_JOB'] as const;
export type InvoiceGrouping = (typeof INVOICE_GROUPINGS)[number];

/** Per-hauler invoice settings — number prefix + the never-reset sequence. */
export const haulerTenantInvoiceSettings = pgTable(
  'hauler_tenant_invoice_settings',
  {
    tenantId: uuid('tenant_id')
      .primaryKey()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    invoiceNumberPrefix: text('invoice_number_prefix').notNull().default('INV'),
    sequence: integer('sequence').notNull().default(0),
    grouping: text('grouping').notNull().default('PER_OPERATOR_PERIOD'),
    taxRateBps: integer('tax_rate_bps').notNull().default(0),
    remitToName: text('remit_to_name'),
    remitToAddress: text('remit_to_address'),
    lineItemDetail: boolean('line_item_detail').notNull().default(true),
    // Migration 0142 (DEC-326) — the company's payment terms in days (Net 30 = 30).
    // A customer's hauler_operators.payment_terms_days overrides it.
    defaultPaymentTermsDays: integer('default_payment_terms_days').notNull().default(30),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  () => [
    check(
      'hauler_tenant_invoice_settings_payment_terms_check',
      sql`default_payment_terms_days BETWEEN 0 AND 365`,
    ),
    check(
      'hauler_tenant_invoice_settings_grouping_check',
      sql`grouping IN ('PER_OPERATOR_PERIOD', 'PER_JOB')`,
    ),
    check('hauler_tenant_invoice_settings_seq_nonneg', sql`sequence >= 0`),
    check('hauler_tenant_invoice_settings_tax_nonneg', sql`tax_rate_bps >= 0`),
    tenantIsolation('hauler_tenant_invoice_settings_isolation'),
  ],
);

export const invoices = pgTable(
  'invoices',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // LEGACY customer identity (jobs.operator_id). Kept for invoices already generated.
    operatorId: uuid('operator_id'),
    // DEC-192 option A (migration 0083) — the CURRENT customer identity, carried from
    // the billable events an invoice is generated from. Invoices for current-model jobs
    // set this and leave operator_id NULL; readers prefer it and fall back.
    haulerOperatorId: uuid('hauler_operator_id'),
    invoiceNumber: text('invoice_number').notNull(),
    status: text('status').notNull().default('DRAFT'),
    grouping: text('grouping').notNull().default('PER_OPERATOR_PERIOD'),
    periodStart: timestamp('period_start', { withTimezone: true }),
    periodEnd: timestamp('period_end', { withTimezone: true }),
    subtotalCents: integer('subtotal_cents').notNull().default(0),
    taxCents: integer('tax_cents').notNull().default(0),
    totalCents: integer('total_cents').notNull().default(0),
    generatedAt: timestamp('generated_at', { withTimezone: true }).notNull().defaultNow(),
    generatedBy: uuid('generated_by').references(() => users.id, { onDelete: 'set null' }),
    sentAt: timestamp('sent_at', { withTimezone: true }),
    paidAt: timestamp('paid_at', { withTimezone: true }),
    voidAt: timestamp('void_at', { withTimezone: true }),
    // DEC-322 (migration 0129) — who voided it and why; who recorded the payment, its
    // reference, and the LOCAL calendar date it arrived (a DATE, not an instant — DEC-004).
    voidedBy: uuid('voided_by').references(() => users.id, { onDelete: 'set null' }),
    voidReason: text('void_reason'),
    paidBy: uuid('paid_by').references(() => users.id, { onDelete: 'set null' }),
    paymentReference: varchar('payment_reference', { length: 100 }),
    paymentDate: date('payment_date', { mode: 'string' }),
    // Migration 0142 (DEC-326) — stamped together when the invoice is SENT: the terms
    // in force (customer override, else company default) and the tenant-LOCAL due
    // date = local sent day + terms. Fixed at send, so a later default change never
    // moves a date the customer was given. Both NULL on a DRAFT.
    paymentTermsDays: integer('payment_terms_days'),
    dueDate: date('due_date', { mode: 'string' }),
    pdfS3Key: text('pdf_s3_key'),
    qboInvoiceId: text('qbo_invoice_id'),
    qboSyncedAt: timestamp('qbo_synced_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex('invoices_number_unique').on(t.tenantId, t.invoiceNumber),
    index('invoices_tenant_operator_idx').on(t.tenantId, t.operatorId),
    // Migration 0083 — invoice lists and the aging report group by customer identity.
    index('invoices_hauler_operator_idx').on(t.haulerOperatorId),
    index('invoices_tenant_status_idx').on(t.tenantId, t.status),
    check('invoices_status_check', sql`status IN ('DRAFT', 'SENT', 'PAID', 'VOID')`),
    check(
      'invoices_payment_terms_pair_check',
      sql`(payment_terms_days IS NULL) = (due_date IS NULL)`,
    ),
    index('invoices_tenant_due_date_sent_idx')
      .on(t.tenantId, t.dueDate)
      .where(sql`status = 'SENT'`),
    tenantIsolation('invoices_isolation'),
  ],
);

export const invoiceLineItems = pgTable(
  'invoice_line_items',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    invoiceId: uuid('invoice_id')
      .notNull()
      .references(() => invoices.id, { onDelete: 'cascade' }),
    jobId: uuid('job_id').references(() => jobs.id, { onDelete: 'set null' }),
    // Traceability back to the source billable_event (no FK — billable_events is the
    // audit-of-record and rows are never deleted; a soft reference is enough).
    billableEventId: uuid('billable_event_id'),
    lineType: text('line_type').notNull(),
    quantity: numeric('quantity').notNull(),
    unitLabel: text('unit_label'),
    rateCents: integer('rate_cents').notNull(),
    amountCents: integer('amount_cents').notNull(),
    sortOrder: integer('sort_order').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    // Migration 0250 (gap 6) — the source event's service date, copied at generation like the
    // quantity and rate: the Date the invoice, the PDF and the QuickBooks export print.
    serviceDate: date('service_date'),
  },
  (t) => [
    index('invoice_line_items_invoice_idx').on(t.invoiceId),
    check('invoice_line_items_line_type_check', sql`line_type IN ('UNIT', 'HOURLY', 'FLAT')`),
    tenantIsolation('invoice_line_items_isolation'),
  ],
);

export const invoiceTickets = pgTable(
  'invoice_tickets',
  {
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    invoiceId: uuid('invoice_id')
      .notNull()
      .references(() => invoices.id, { onDelete: 'cascade' }),
    jobId: uuid('job_id')
      .notNull()
      .references(() => jobs.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    primaryKey({ columns: [t.invoiceId, t.jobId] }),
    tenantIsolation('invoice_tickets_isolation'),
  ],
);

export type Invoice = typeof invoices.$inferSelect;
export type NewInvoice = typeof invoices.$inferInsert;
export type InvoiceLineItem = typeof invoiceLineItems.$inferSelect;
export const invoiceSelectSchema = createSelectSchema(invoices);
export const invoiceInsertSchema = createInsertSchema(invoices);
